import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.#enforceColorIsolation();

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.nextElementSibling?.matches('.limitless-product-media-pending[data-gallery-generated="true"]')) {
      this.nextElementSibling.remove();
    }
    this.replaceWith(newMediaGallery);
  };

  /**
   * Final client-side safety guard for products with multiple color groups.
   * If Shopify's initial render returns a generic gallery for a color that has
   * no verified media, hide it instead of exposing images carrying markers for
   * other colors. The component remains connected so a later color selection
   * can replace it normally.
   */
  #enforceColorIsolation() {
    const section = this.closest('.shopify-section');
    const fieldsets = Array.from(section?.querySelectorAll('variant-picker fieldset') ?? []);
    const colorFieldset = fieldsets.find(
      (fieldset) => fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() === 'color'
    );
    const selectedColor = colorFieldset?.querySelector('input:checked')?.value?.trim();

    if (!selectedColor) return;

    const marker = `[LIMITLESS_COLOR:${selectedColor}]`;
    const imageAlts = Array.from(this.querySelectorAll('img')).map((image) => image.alt || '');
    const hasSelectedColorMedia = imageAlts.some((alt) => alt.includes(marker));
    const hasOtherColorMedia = imageAlts.some(
      (alt) => alt.includes('[LIMITLESS_COLOR:') && !alt.includes(marker)
    );

    if (hasSelectedColorMedia || !hasOtherColorMedia) return;

    this.hidden = true;

    const placeholder = document.createElement('div');
    placeholder.className = 'limitless-product-media-pending spacing-style sticky-content';
    placeholder.dataset.galleryGenerated = 'true';
    placeholder.setAttribute('role', 'img');
    placeholder.setAttribute('aria-label', `Imagen pendiente para ${selectedColor}`);
    const pendingImageUrl = this.dataset.pendingImageUrl;
    const pendingLogoUrl = this.dataset.pendingLogoUrl;

    if (pendingLogoUrl) {
      const logoWrapper = document.createElement('div');
      logoWrapper.className = 'limitless-placeholder-logo';
      logoWrapper.setAttribute('aria-hidden', 'true');

      const logo = document.createElement('img');
      logo.className = 'limitless-placeholder-logo__image';
      logo.src = pendingLogoUrl;
      logo.alt = '';
      logo.width = 1200;
      logo.height = 1200;
      logoWrapper.append(logo);
      placeholder.append(logoWrapper);
    }

    if (pendingImageUrl) {
      const image = document.createElement('img');
      image.className = 'limitless-product-media-pending__placeholder';
      image.src = pendingImageUrl;
      image.alt = `Imagen no disponible por el momento para el color ${selectedColor}`;
      image.width = 1003;
      image.height = 1568;
      placeholder.append(image);
    } else {
      const message = document.createElement('p');
      message.textContent = `Imagen pendiente para ${selectedColor}`;
      placeholder.append(message);
    }
    this.insertAdjacentElement('afterend', placeholder);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
